'use client'

import clsx from 'clsx'
import type { ReactNode } from 'react'

import CheckSmIcon from '@/assets/icons/check-sm.svg'
import { DropdownCheckIcon } from '@/ui/icons/DropdownCheckIcon'

import S from './TasksFeedNav.module.scss'

export type FeedFilterOption = {
  value: string
  label: string
  icon?: ReactNode
}

/** A multi-select filter of the feed: Platforms, Task type. */
export type FeedMultiFilter = {
  id: string
  label: string
  allLabel: string
  ariaLabel: string
  groups: { label?: string; options: FeedFilterOption[] }[]
  value: readonly string[]
  onChange: (next: string[]) => void
}

export type FeedSortOption = { value: string; label: string }

type FeedCheckListProps = {
  filter: Pick<FeedMultiFilter, 'allLabel' | 'groups' | 'ariaLabel'>
  value: readonly string[]
  onChange: (next: string[]) => void
  size?: 'menu' | 'sheet'
}

/** «All …» on top, then the options with checkboxes, grouped. */
export function FeedCheckList({
  filter,
  value,
  onChange,
  size = 'menu'
}: FeedCheckListProps) {
  const toggle = (option: string) => {
    onChange(
      value.includes(option)
        ? value.filter(v => v !== option)
        : [...value, option]
    )
  }

  return (
    <div
      className={clsx(S.checkList, size === 'sheet' && S.checkListSheet)}
      role="group"
      aria-label={filter.ariaLabel}
    >
      <button
        type="button"
        className={clsx(S.menuItem, value.length === 0 && S.menuItemAll)}
        aria-pressed={value.length === 0}
        onClick={() => {
          onChange([])
        }}
      >
        <span className={S.menuItemLabel}>{filter.allLabel}</span>
      </button>
      {filter.groups.map((group, index) => (
        <div key={group.label ?? index} className={S.menuGroup}>
          {group.label ? (
            <span className={S.menuGroupLabel}>{group.label}</span>
          ) : null}
          {group.options.map(option => {
            const checked = value.includes(option.value)
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                className={S.menuItem}
                onClick={() => {
                  toggle(option.value)
                }}
              >
                <span
                  className={clsx(S.checkbox, checked && S.checkboxChecked)}
                  aria-hidden
                >
                  {checked ? <CheckSmIcon className={S.checkboxIcon} /> : null}
                </span>
                {option.icon ? (
                  <span className={S.menuItemIcon} aria-hidden>
                    {option.icon}
                  </span>
                ) : null}
                <span className={S.menuItemLabel}>{option.label}</span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

type FeedSortListProps = {
  options: FeedSortOption[]
  value: string
  onChange: (next: string) => void
  ariaLabel: string
  size?: 'menu' | 'sheet'
}

export function FeedSortList({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'menu'
}: FeedSortListProps) {
  return (
    <div
      className={clsx(S.checkList, size === 'sheet' && S.checkListSheet)}
      role="listbox"
      aria-label={ariaLabel}
    >
      {options.map(option => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={selected}
            className={clsx(S.menuItem, selected && S.menuItemSelected)}
            onClick={() => {
              onChange(option.value)
            }}
          >
            <span className={S.menuItemLabel}>{option.label}</span>
            {selected ? (
              <DropdownCheckIcon className={S.menuItemCheck} />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
